import { ReactNode, useId } from 'react';

import { Primary, White } from '../../../styles/Colors';
import Button from '../../Button/Button';
import Modal, { ModalProps } from '../Modal';
import ModalBody from '../ModalBody';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';

interface ConfirmModalProps
  extends Pick<
    ModalProps,
    'isOpen' | 'isPortal' | 'hideBackdrop' | 'backdropColor' | 'className'
  > {
  onConfirm: () => void;
  onCancel: () => void;
  title?: ReactNode;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  // Acknowledgement-only usage: the modal keeps a single dismiss button.
  hideCancel?: boolean;
}

const ConfirmModal = ({
  isOpen,
  isPortal,
  title,
  message,
  onConfirm,
  confirmText,
  onCancel,
  cancelText,
  hideCancel = false,
  backdropColor,
  hideBackdrop,
  className,
}: ConfirmModalProps) => {
  const id = useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;

  return (
    <Modal
      isOpen={isOpen}
      isPortal={isPortal}
      onClose={onCancel}
      backdropColor={backdropColor}
      hideBackdrop={hideBackdrop}
      hideCloseButton={!title}
      className={className}
      // A single dismiss action means there is nothing to decide — that is an
      // acknowledgement, and assistive technology should interrupt for it
      // (ISSUE-009's duplicate-file warning).
      role={hideCancel ? 'alertdialog' : 'dialog'}
      // No title means no ModalHeader is rendered at all, so the message is
      // what names the dialog — never leave it anonymous.
      aria-labelledby={title ? titleId : bodyId}
      aria-describedby={bodyId}
    >
      {title && <ModalHeader id={titleId}>{title}</ModalHeader>}
      <ModalBody id={bodyId}>{message}</ModalBody>
      <ModalFooter>
        {!hideCancel && (
          <Button color={White} variant="outlined" onClick={onCancel}>
            {cancelText || 'Cancel'}
          </Button>
        )}
        <Button color={Primary[400]} onClick={onConfirm}>
          {confirmText || 'Confirm'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmModal;
